import { getImageSRC } from '@core/images-ui/utils';
import bronzeGem from './gems/c_Bronze_1.svg';
import diamondGem from './gems/c_Diamond_0.svg';
import goldGem from './gems/c_Gold_1.svg';
import platinumGem from './gems/c_Platinum_0.svg';
import silverGem from './gems/c_Silver_1.svg';

// `?modal=<CLUB_PELOTON_MODAL_NAME>` opens the score modal on load, so emails
// and other entry points can deep-link straight to it. Matches the app's
// shared modal-param convention (see @members/modal/urls MODAL_PARAM).
export const CLUB_PELOTON_MODAL_NAME = 'clubPeloton';

// NOTE: Copy is hardcoded (English) for the initial rollout. Localization keys
// should be added to the @members copy pipeline as a follow-up so these strings
// resolve through the standard i18n codegen.
export const CLUB_PELOTON_COPY = {
  title: 'Club Peloton',
  points: 'points',
  yourProgress: 'Your progress',
  yourProgressSubtitle: 'Unlock perks, rewards, and recognition as you level up.',
  learnMore:
    'Learn about how Club Peloton works, how to earn points, and more on the Peloton App.',
  openApp: 'Open app',
} as const;

// The five major Club Peloton tiers, in ascending order. `badgeKey` maps to a
// valid `mapLevelToBadge` sprite key (sub-level tiers reuse the "I" sprite).
// `lockedGemUrl` is that tier's gem silhouette, used as a mask for the locked
// (not-yet-earned) state — matches the club-peloton app's CFXView tier cards,
// where Champion/Legend map to the Platinum/Diamond gem shapes.
export const TIER_CARDS = [
  { name: 'Bronze', badgeKey: 'Bronze I', lockedGemUrl: getImageSRC(bronzeGem) },
  { name: 'Silver', badgeKey: 'Silver I', lockedGemUrl: getImageSRC(silverGem) },
  { name: 'Gold', badgeKey: 'Gold I', lockedGemUrl: getImageSRC(goldGem) },
  { name: 'Champion', badgeKey: 'Champion', lockedGemUrl: getImageSRC(platinumGem) },
  { name: 'Legend', badgeKey: 'Legend', lockedGemUrl: getImageSRC(diamondGem) },
] as const;

const ROMAN_SUFFIXES = ['I', 'II', 'III'] as const;

const SUBLEVEL_INDEX: Record<string, number> = { I: 0, II: 1, III: 2 };

/** The major tier word ("Gold") from a full level name ("Gold II"). */
export const getMajorTier = (badgeLevelName?: string): string =>
  (badgeLevelName ?? '').split(' ')[0] ?? '';

/** Ascending rank (0=Bronze … 4=Legend) of a level's major tier, or -1. */
export const getTierRank = (badgeLevelName?: string): number =>
  TIER_CARDS.findIndex(tier => tier.name === getMajorTier(badgeLevelName));

/**
 * How many gems to show and how many are filled for the level. Sub-level tiers
 * (Bronze/Silver/Gold) show 3 gems with the achieved sub-levels filled; the
 * single-name tiers (Champion/Legend) show one filled gem.
 */
export const getGemDisplay = (
  badgeLevelName?: string,
): { total: number; filled: number } => {
  if (!badgeLevelName) {
    return { total: 3, filled: 0 };
  }
  const suffix = badgeLevelName.split(' ')[1];
  if (suffix && suffix in SUBLEVEL_INDEX) {
    return { total: 3, filled: SUBLEVEL_INDEX[suffix] + 1 };
  }
  return { total: 1, filled: 1 };
};

/** Segment labels under the modal progress bar ("Gold I" | "Gold II" | "Gold III"). */
export const getSegmentLabels = (badgeLevelName?: string): string[] => {
  const major = getMajorTier(badgeLevelName);
  const suffix = (badgeLevelName ?? '').split(' ')[1];
  if (!major || !(suffix && suffix in SUBLEVEL_INDEX)) {
    return badgeLevelName ? [badgeLevelName] : [];
  }
  return ROMAN_SUFFIXES.map(roman => `${major} ${roman}`);
};
